import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * useMarketsSummary — lecture directe du payload /api/markets/summary.
 *
 * Expose les 13 indicateurs bruts retournés par le backend (shape API native,
 * pas le shape mock de /marches). Conçu pour le widget home qui a besoin
 * d'afficher tous les indicateurs dans un ordre imposé.
 *
 * Ne remplace pas useMarketData : les deux hooks coexistent. La page /marches
 * utilise useMarketData (pour ses 8 collections), la home utilise ce hook-ci
 * (pour sa liste plate triée). Sur une même page il n'y en a qu'un actif
 * à la fois, donc pas de double fetch en pratique.
 *
 * Polling 60s (aligné cron backend). Lecture cache Mongo <100ms.
 *
 * Retour :
 *   { indicators, updatedAt, cacheAge, stale, sourceHealth, loading, error }
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FETCH_INTERVAL_MS = 60000;

export default function useMarketsSummary() {
  const [state, setState] = useState({
    indicators: [],
    updatedAt: null,
    cacheAge: null,
    stale: false,
    sourceHealth: null,
    loading: true,
    error: null,
  });
  const timerRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/markets/summary`, { timeout: 5000 });
      const d = res.data || {};
      setState({
        indicators: Array.isArray(d.indicators) ? d.indicators : [],
        updatedAt: d.updated_at || null,
        cacheAge: typeof d.cache_age_seconds === 'number' ? d.cache_age_seconds : null,
        stale: !!d.stale,
        sourceHealth: d.source_health || null,
        loading: false,
        error: null,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.message || 'fetch error' }));
    }
  }, []);

  useEffect(() => {
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, FETCH_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchOnce]);

  return state;
}
